// Clavier d'essai : touches de l'ordinateur et clics sur le clavier affiché.
// Sert à vérifier l'application sans piano et à essayer un exercice avant de
// brancher son instrument. Les touches sont repérées par leur position physique
// (event.code), donc identiques en AZERTY et en QWERTY.

import { BaseInputSource, now } from './types';

/** Rangée du milieu (Q S D F G H J K L M en AZERTY, A S D F G H J K L ; en QWERTY) = touches blanches. */
const KEY_TO_SEMITONE: Record<string, number> = {
  KeyA: 0, // do
  KeyW: 1, // do♯ (rangée du haut)
  KeyS: 2, // ré
  KeyE: 3, // ré♯
  KeyD: 4, // mi
  KeyF: 5, // fa
  KeyT: 6, // fa♯
  KeyG: 7, // sol
  KeyY: 8, // sol♯
  KeyH: 9, // la
  KeyU: 10, // la♯
  KeyJ: 11, // si
  KeyK: 12, // do (octave suivante)
  KeyO: 13,
  KeyL: 14,
  KeyP: 15,
  Semicolon: 16,
};

/** Secours quand l'événement ne porte pas de code physique (certains outils d'automatisation) : lettres QWERTY. */
const KEY_FALLBACK: Record<string, string> = {
  a: 'KeyA', w: 'KeyW', s: 'KeyS', e: 'KeyE', d: 'KeyD', f: 'KeyF', t: 'KeyT', g: 'KeyG', y: 'KeyY', h: 'KeyH', u: 'KeyU', j: 'KeyJ', k: 'KeyK', o: 'KeyO', l: 'KeyL', p: 'KeyP', ';': 'Semicolon', z: 'KeyZ', x: 'KeyX',
};

function physicalCode(e: KeyboardEvent): string {
  if (e.code) return e.code;
  return KEY_FALLBACK[e.key.toLowerCase()] ?? '';
}

function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

export class KeyboardInputSource extends BaseInputSource {
  private base = 60;
  private readonly held = new Map<string, number>();
  private readonly pressed = new Set<number>();
  private listening = false;

  constructor() {
    super('keyboard');
  }

  get baseOctavePitch(): number {
    return this.base;
  }

  async start(): Promise<void> {
    if (!this.listening && typeof window !== 'undefined') {
      window.addEventListener('keydown', this.onKeyDown);
      window.addEventListener('keyup', this.onKeyUp);
      window.addEventListener('blur', this.releaseAll);
      this.listening = true;
    }
    this.setStatus({ state: 'active', message: 'Clavier d’essai actif : rangée du milieu du clavier = touches blanches à partir du do central, rangée du haut = touches noires. Les deux touches en bas à gauche changent d’octave. Vous pouvez aussi cliquer sur le clavier affiché.' });
  }

  stop(): void {
    if (this.listening && typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.onKeyDown);
      window.removeEventListener('keyup', this.onKeyUp);
      window.removeEventListener('blur', this.releaseAll);
      this.listening = false;
    }
    this.releaseAll();
    this.setStatus({ state: 'idle', message: '' });
  }

  press(pitch: number): void {
    // Une touche déjà enfoncée (relâchement non reçu) est relâchée puis rejouée : chaque frappe compte.
    if (this.pressed.has(pitch)) this.release(pitch);
    this.pressed.add(pitch);
    this.emit({ type: 'noteOn', pitch, time: now(), velocity: 0.8, confidence: 'sure', source: 'keyboard' });
  }

  release(pitch: number): void {
    if (!this.pressed.delete(pitch)) return;
    this.emit({ type: 'noteOff', pitch, time: now(), source: 'keyboard' });
  }

  /** Simule une perte puis un retour de connexion (essais manuels du mode pause). */
  simulateConnection(state: 'lost' | 'restored'): void {
    this.emit({ type: 'connection', state, time: now(), source: 'keyboard', detail: state === 'lost' ? 'perte simulée' : 'retour simulé' });
  }

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.repeat || e.ctrlKey || e.metaKey || e.altKey || isEditable(e.target)) return;
    const code = physicalCode(e);
    if (code === 'KeyZ') {
      this.base = Math.max(24, this.base - 12);
      return;
    }
    if (code === 'KeyX') {
      this.base = Math.min(96, this.base + 12);
      return;
    }
    const semitone = KEY_TO_SEMITONE[code];
    if (semitone === undefined) return;
    e.preventDefault();
    const pitch = this.base + semitone;
    this.held.set(code, pitch);
    this.press(pitch);
  };

  private readonly onKeyUp = (e: KeyboardEvent): void => {
    const code = physicalCode(e);
    const pitch = this.held.get(code);
    if (pitch === undefined) return;
    this.held.delete(code);
    this.release(pitch);
  };

  private readonly releaseAll = (): void => {
    for (const pitch of [...this.pressed]) this.release(pitch);
    this.held.clear();
  };
}
